// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — how up to date a live stream is (Situation room, slice CR2,
 * docs/AYNI_XR_CONTROL_ROOM_PLAN.md). A duty officer reads "how old is what
 * I see" before reading the map, so every layer in the room says it:
 *
 * - **Observations** (`real-time`): a frame is stamped at the start of its
 *   period, published only after the period ends and is processed, and
 *   stays the newest until the next period has ended and been processed
 *   too. So a daily stream's newest frame is normally between one and two
 *   days old. The stream is *on time* while its newest frame is no older
 *   than two periods plus six hours of processing (`grace`); each further
 *   period without a new frame is a release missed. One missed: *late*
 *   (amber); two or more: *stale* (red). The period is the stream's frame
 *   cadence, never under an hour (releases are not published more often).
 *   (Browser check, 2026-10-11: one period plus six hours called FIRMS late
 *   at 30 hours, when the next day's frame cannot exist yet.)
 * - **Forecasts**: the newest frame lies in the future, so the age is the
 *   run's: its first frame. A run is expected every twelve hours with twelve
 *   hours to publish: on time up to a day old, late up to a day and a half,
 *   stale after.
 * - Anything else (archives, projections, reanalyses) has no freshness: it
 *   is not meant to move.
 *
 * Pure: the caller reads the stream's descriptor.
 */

export type FreshnessLevel = 'fresh' | 'late' | 'stale' | 'unknown'

export interface FreshnessInput {
  /** The newest frame's date, epoch ms; null when the descriptor could not be read. */
  lastFrameMs: number | null
  /** The first frame's date (a forecast's run), epoch ms. */
  firstFrameMs: number | null
  /** Time between frames, ms. */
  cadenceMs: number | null
  kind: 'real-time' | 'forecast'
}

export interface Freshness {
  level: FreshnessLevel
  /** How old the data is: the newest frame (observations) or the run (forecasts), ms; null unknown. */
  ageMs: number | null
  /** Releases missed (0 on time). */
  missed: number
  /** The date the age is measured from, epoch ms. */
  asOfMs: number | null
}

const HOUR = 3_600_000
/** Processing after a period ends before its frame is published. */
export const FRESHNESS_PROCESSING_MS = 6 * HOUR
/** A forecast run's spacing and its publishing allowance. */
export const FORECAST_RUN_MS = 12 * HOUR

export function streamFreshness(input: FreshnessInput, nowMs: number): Freshness {
  const forecast = input.kind === 'forecast'
  const asOfMs = forecast ? input.firstFrameMs : input.lastFrameMs
  if (asOfMs === null || !Number.isFinite(asOfMs)) return { level: 'unknown', ageMs: null, missed: 0, asOfMs: null }
  const ageMs = Math.max(0, nowMs - asOfMs)
  let interval: number
  let grace: number
  if (forecast) {
    interval = FORECAST_RUN_MS
    grace = 2 * FORECAST_RUN_MS
  } else {
    if (input.cadenceMs === null || !Number.isFinite(input.cadenceMs) || input.cadenceMs <= 0) {
      return { level: 'unknown', ageMs, missed: 0, asOfMs }
    }
    interval = Math.max(input.cadenceMs, HOUR)
    grace = 2 * interval + FRESHNESS_PROCESSING_MS
  }
  const missed = ageMs <= grace ? 0 : Math.ceil((ageMs - grace) / interval)
  const level: FreshnessLevel = missed === 0 ? 'fresh' : missed === 1 ? 'late' : 'stale'
  return { level, ageMs, missed, asOfMs }
}

/** The worst of several levels (the room's line): stale over late over unknown over fresh. */
export function worstFreshness(levels: readonly FreshnessLevel[]): FreshnessLevel | null {
  if (levels.length === 0) return null
  const rank: Record<FreshnessLevel, number> = { fresh: 0, unknown: 1, late: 2, stale: 3 }
  return levels.reduce((a, b) => (rank[b] > rank[a] ? b : a))
}

/** An age for people, in the largest whole unit: { n, unit }. */
export function ageParts(ageMs: number): { n: number; unit: 'minutes' | 'hours' | 'days' } {
  const minutes = Math.floor(ageMs / 60_000)
  if (minutes < 120) return { n: Math.max(0, minutes), unit: 'minutes' }
  const hours = Math.floor(ageMs / HOUR)
  if (hours < 48) return { n: hours, unit: 'hours' }
  return { n: Math.floor(ageMs / (24 * HOUR)), unit: 'days' }
}
